import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Calendar, DollarSign, Users, TrendingUp, Download, Clock, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { SubscriptionError } from '@/types/subscription';
import { useSubscription } from '@/contexts/subscription-context';
import { UpgradeDialog } from '@/components/subscription/upgrade-dialog';
import { useSetPageHeader } from '@/components/chrome/PageHeader';

type Period = '7days' | '30days' | '90days' | 'year';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down';
}

function MetricCard({ title, value, change, icon, trend }: MetricCardProps) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm md:text-base text-soft font-medium">{title}</p>
            <p className="text-2xl md:text-3xl font-bold mt-2">{value}</p>
            {change && (
              <p className={`text-sm mt-2 flex items-center gap-1 ${
                trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-soft'
              }`}>
                {trend === 'up' && '↑'}
                {trend === 'down' && '↓'}
                {change}
              </p>
            )}
          </div>
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [period, setPeriod] = useState<Period>('30days');
  const [upgradeError, setUpgradeError] = useState<SubscriptionError | null>(null);
  const { isFeatureEnabled, limits } = useSubscription();

  // Mock data - replace with actual API calls
  const metrics = {
    totalBookings: { value: 127, change: '+12%', trend: 'up' as const },
    revenue: { value: '€3,420', change: '+8%', trend: 'up' as const },
    activeCustomers: { value: 45, change: '+5', trend: 'up' as const },
    avgBookingValue: { value: '€110', change: '-2%', trend: 'down' as const },
  };

  const topServices = [
    { name: 'Therapy', bookings: 45, revenue: '€4,500' },
    { name: 'Therapy Deluxe', bookings: 32, revenue: '€3,936' },
    { name: 'Appointment', bookings: 28, revenue: '€3,080' },
  ];

  const isLocked = !isFeatureEnabled('advancedAnalytics');

  useSetPageHeader(
    {
      title: t('admin:reports.title'),
      sub: t('admin:reports.subtitle'),
      actions: (
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={(value) => setPeriod(value as Period)} disabled={isLocked}>
            <SelectTrigger className="w-full md:w-[180px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="year">{t('admin:reports.filters.thisYear')}</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="md" disabled={isLocked} iconLeft={<Download className="h-4 w-4" />}>
            <span className="hidden md:inline">{t('admin:reports.export')}</span>
          </Button>
        </div>
      ),
    },
    [t, period, isLocked],
  );

  return (
    <div className="space-y-6">
      {/* Feature Gate Alert */}
      {isLocked && (
        <Alert className="mb-6 bg-purple-50 border-purple-200">
          <Lock className="h-5 w-5 text-purple-600" />
          <AlertDescription className="ml-2">
            <div className="flex flex-col gap-3">
              <div>
                <h3 className="text-h3 text-ink mb-1">Advanced Analytics - Premium Feature</h3>
                <p className="text-body-sm text-soft">
                  Unlock detailed business insights, revenue tracking, customer behavior analysis, and advanced reporting capabilities.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                <div className="flex items-center gap-1 text-soft">
                  <TrendingUp className="h-4 w-4" />
                  <span>{t('admin:reports.premium.bullets.revenueForecasting')}</span>
                </div>
                <div className="flex items-center gap-1 text-soft">
                  <Users className="h-4 w-4" />
                  <span>{t('admin:reports.premium.bullets.customerInsights')}</span>
                </div>
                <div className="flex items-center gap-1 text-soft">
                  <BarChart3 className="h-4 w-4" />
                  <span>{t('admin:reports.premium.bullets.customReports')}</span>
                </div>
                <div className="flex items-center gap-1 text-soft">
                  <Download className="h-4 w-4" />
                  <span>{t('admin:reports.premium.bullets.dataExport')}</span>
                </div>
              </div>
              <Button
                size="sm"
                className="w-fit bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 text-white"
                onClick={() => {
                  setUpgradeError({
                    type: 'feature_disabled',
                    feature: 'advanced_analytics',
                    featureName: 'Advanced Analytics',
                    message: 'Advanced analytics are not available on your current plan',
                    currentPlan: limits?.currentPlan.name,
                    recommendedPlan: 'Pro',
                    description: 'Upgrade to Pro plan to unlock detailed insights, revenue tracking, and custom reports.',
                  });
                }}
              >
                Upgrade to Unlock Advanced Analytics
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Content with blur overlay when locked */}
      <div className={`relative ${isLocked ? 'pointer-events-none' : ''}`}>
        {isLocked && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
            <Card className="max-w-md mx-4 shadow-xl">
              <CardContent className="p-6 text-center">
                <Lock className="h-12 w-12 mx-auto mb-4 text-brand-500" />
                <h3 className="text-h2 text-ink mb-2">{t('admin:reports.premium.title')}</h3>
                <p className="text-soft mb-4">
                  Advanced analytics and reporting features are available on Pro and Enterprise plans.
                </p>
                <Button
                  className="bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 text-white"
                  onClick={() => {
                    setUpgradeError({
                      type: 'feature_disabled',
                      feature: 'advanced_analytics',
                      featureName: 'Advanced Analytics',
                      message: 'Advanced analytics are not available on your current plan',
                      currentPlan: limits?.currentPlan.name,
                      recommendedPlan: 'Pro',
                      description: 'Upgrade to Pro plan to unlock detailed insights, revenue tracking, and custom reports.',
                    });
                  }}
                >
                  View Upgrade Options
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Key Metrics */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 ${isLocked ? 'blur-sm' : ''}`}>
        <MetricCard
          title={t('admin:reports.kpis.totalBookings')}
          value={metrics.totalBookings.value}
          change={metrics.totalBookings.change}
          trend={metrics.totalBookings.trend}
          icon={<Calendar className="w-6 h-6 md:w-7 md:h-7 text-brand-600" />}
        />
        <MetricCard
          title={t('admin:reports.kpis.revenue')}
          value={metrics.revenue.value}
          change={metrics.revenue.change}
          trend={metrics.revenue.trend}
          icon={<DollarSign className="w-6 h-6 md:w-7 md:h-7 text-green-600" />}
        />
        <MetricCard
          title={t('admin:reports.kpis.activeCustomers')}
          value={metrics.activeCustomers.value}
          change={metrics.activeCustomers.change}
          trend={metrics.activeCustomers.trend}
          icon={<Users className="w-6 h-6 md:w-7 md:h-7 text-purple-600" />}
        />
        <MetricCard
          title={t('admin:reports.kpis.avgBookingValue')}
          value={metrics.avgBookingValue.value}
          change={metrics.avgBookingValue.change}
          trend={metrics.avgBookingValue.trend}
          icon={<TrendingUp className="w-6 h-6 md:w-7 md:h-7 text-orange-600" />}
        />
      </div>

        {/* Top Services */}
        <Card className={isLocked ? 'blur-sm' : ''}>
        <CardHeader>
          <CardTitle>{t('admin:reports.topServices')}</CardTitle>
          <CardDescription className="text-sm md:text-base">
            Most popular services for the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topServices.map((service, index) => (
              <div
                key={service.name}
                className="flex items-center justify-between p-4 bg-surface-2 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-base md:text-lg">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-base md:text-lg">{service.name}</p>
                    <p className="text-sm md:text-base text-soft">{service.bookings} bookings</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-base md:text-lg text-green-600">{service.revenue}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

        {/* Booking Trends Chart Placeholder */}
        <Card className={`mt-6 ${isLocked ? 'blur-sm' : ''}`}>
        <CardHeader>
          <CardTitle>{t('admin:reports.bookingTrends')}</CardTitle>
          <CardDescription className="text-sm md:text-base">
            Daily bookings over the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 md:h-80 flex items-center justify-center bg-surface-2 rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 md:w-16 md:h-16 mx-auto text-soft mb-3" />
              <p className="text-sm md:text-base text-soft">
                Chart visualization coming soon
              </p>
              <p className="text-xs md:text-sm text-soft mt-1">
                Integration with charting library required
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Upgrade Dialog */}
      {upgradeError && (
        <UpgradeDialog
          open={!!upgradeError}
          onClose={() => setUpgradeError(null)}
          error={upgradeError}
        />
      )}
    </div>
  );
}
